import React from 'react';
import { motion } from 'motion/react';
import { Target, Activity, Zap, ShieldCheck, Search, Filter, Cpu, ArrowUpRight } from 'lucide-react';

export default function VulnerabilityManagementPolicy() {
  const lastUpdated = "April 21, 2026";

  const managementPillars = [
    {
      title: "System Scanning",
      description: "Our infrastructure undergoes continuous, automated scanning to identify potential security weaknesses and surface-level exposures before they can be exploited.",
      icon: Search
    },
    {
      title: "Risk-Based Triage",
      description: "Identified vulnerabilities are not just counted; they are analyzed. We prioritize remediation based on CVSS scores, business context, and potential exploitability.",
      icon: Filter
    },
    {
      title: "Rapid Remediation",
      description: "Critical vulnerabilities are addressed with immediate priority. Our response teams are empowered to deploy hotfixes outside of standard release cycles for high-risk findings.",
      icon: Zap
    },
    {
      title: "Patch Orchestration",
      description: "Security updates and component patches are applied through automated, verified pipelines with regression testing to ensure continuous protection without regressions.",
      icon: Cpu
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Proactive Defense
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Vulnerability <span className="text-accent">Management</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Agrandintl proactively identifies, triages, and remediates system vulnerabilities to ensure continuous protection against emerging threats.
            </p>
          </div>

          {/* Core Defensive Visual */}
          <div className="glass-card p-12 rounded-[2.5rem] border-white/5 bg-white/[0.01] relative overflow-hidden text-center group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 blur-[100px] rounded-full group-hover:bg-accent/20 transition-colors duration-700" />
            <div className="space-y-6 relative z-10">
              <div className="w-16 h-16 rounded-3xl bg-accent/10 flex items-center justify-center text-accent mx-auto mb-4 border border-accent/20">
                <Target className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-display font-bold">Continuous Protection Loop</h2>
              <p className="text-muted-foreground leading-relaxed text-base max-w-2xl mx-auto">
                "Our vulnerability management lifecycle is a continuous loop of identification and resolution. By addressing weaknesses at the source, we maintain a hardened security posture that evolves faster than the threat landscape."
              </p>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {managementPillars.map((pillar, idx) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] hover:border-accent/10 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-accent group-hover:scale-110 transition-transform">
                      <pillar.icon className="w-5 h-5" />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-accent transition-colors" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-display font-bold">{pillar.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Technical Specs Footer */}
          <div className="pt-12 border-t border-white/5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Scan Freq", value: "Real-time" },
                { label: "SLA (Critical)", value: "< 24h" },
                { label: "Patching", value: "Verified" },
                { label: "Pentesting", value: "Annual" }
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="text-[9px] uppercase font-bold tracking-widest text-muted-foreground mb-1">{stat.label}</div>
                  <div className="text-sm font-mono font-bold text-accent">{stat.value}</div>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-accent" />
                <span className="text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">Hardened Architecture Approved</span>
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">
                Policy Ref: AG-VM-2026 | Last Updated: {lastUpdated}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
